import { type Query, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import type { DisplayLoyaltyProgramRules, DisplayLoyaltyStatus } from "@/api/orval/model";
import {
  getRetrieveLoyaltyStatusQueryKey,
  useRetrieveLoyaltyStatus,
} from "@/api/orval/reward/reward";

export type EnabledLoyaltyStatus = DisplayLoyaltyStatus & { rules: DisplayLoyaltyProgramRules };

type UseLoyaltyStatusArgs = {
  /** The platform setting `loyaltyProgramEnabled`, from the caller's platform read. */
  programEnabled: boolean;
  /** Guests and anonymous visitors have no status to show. */
  isSignedInPatient: boolean;
};

// A visit or purchase promotes the client on the server a moment after it saves, so the first read
// can still show the old tier with no visits left to the next one. Same bounds as Admin's
// refreshLoyaltyStatus.
export const PROMOTION_RETRY_DELAY_MS = 750;
export const PROMOTION_RETRY_LIMIT = 8;

/** The client has reached their next tier but the promotion has not been written yet. */
const isPromotionDue = (status: DisplayLoyaltyStatus | undefined) =>
  status?.isEnabled === true && status.nextTier != null && status.visitsToNextTier === 0;

const toEnabled = (status: DisplayLoyaltyStatus | undefined): EnabledLoyaltyStatus | undefined => {
  if (!status?.isEnabled) return undefined;
  if (!status.rules) {
    throw new Error("Loyalty status is enabled but carries no program rules");
  }
  return { ...status, rules: status.rules };
};

/**
 * The signed-in patient's loyalty status, or undefined while it loads, when the practice runs no
 * loyalty program, or when there is no signed-in patient.
 */
export const useLoyaltyStatus = ({
  programEnabled,
  isSignedInPatient,
}: UseLoyaltyStatusArgs): EnabledLoyaltyStatus | undefined => {
  const { data: status } = useRetrieveLoyaltyStatus({
    query: { enabled: programEnabled && isSignedInPatient },
  });
  return toEnabled(status);
};

/**
 * The status read right after a check-in or checkout: it refetches on mount, then, while the
 * client has reached their next tier but the promotion has not landed, a few more times so a
 * promotion (and its celebration) shows on this screen rather than on the next app open.
 */
export const useLoyaltyStatusAfterActivity = ({
  programEnabled,
  isSignedInPatient,
}: UseLoyaltyStatusArgs): EnabledLoyaltyStatus | undefined => {
  const queryClient = useQueryClient();
  const [updatesBeforeActivity] = useState(
    () => queryClient.getQueryState(getRetrieveLoyaltyStatusQueryKey())?.dataUpdateCount ?? 0,
  );

  const refetchWhilePromotionDue = (query: Query<DisplayLoyaltyStatus>) => {
    const refetches = query.state.dataUpdateCount - updatesBeforeActivity;
    return isPromotionDue(query.state.data) && refetches <= PROMOTION_RETRY_LIMIT
      ? PROMOTION_RETRY_DELAY_MS
      : false;
  };

  const { data: status } = useRetrieveLoyaltyStatus({
    query: {
      enabled: programEnabled && isSignedInPatient,
      refetchOnMount: "always",
      refetchInterval: refetchWhilePromotionDue,
    },
  });
  return toEnabled(status);
};
